import React, { useState, useCallback, useEffect, useMemo } from 'react';
import type { Profile, ProfileUpdateReq } from '@interlucent/organization-protocol';
import { legacyFlagsToOrchestratorArgs } from '@interlucent/organization-protocol';
import { id3 } from '@interlucent/ids';
import { Modal, FormField } from '../shared';
import { profileService } from '../../services/profileService';
import { CapacityScheduleEditor } from './CapacityScheduleEditor';
import { InfrastructureEditor } from './InfrastructureEditor';
import { SUPER_REGION_OPTIONS } from '../../constants/regions';
import type { ProfileFormData, CapacitySchedule, InfrastructureFormData, BackendFormData } from '../../types/profile.types';

function buildSettingsJson(infra: InfrastructureFormData): Record<string, any> {
  return {
    backends: infra.backends.map((b: BackendFormData) => {
      const backend: Record<string, any> = {
        id: b.id,
        cloud: b.cloud,
        region: b.region,
        instanceType: b.instanceType,
        imageId: b.imageId,
        weight: b.weight,
      };
      if (b.maxCapacity != null) backend.maxCapacity = b.maxCapacity;
      if (b.maxConcurrent != null && b.maxConcurrent > 1) backend.maxConcurrent = b.maxConcurrent;
      if (b.startupMode && b.startupMode !== 'standard') backend.startupMode = b.startupMode;
      if (b.launchProtocolVersion != null && b.launchProtocolVersion !== 1) backend.launchProtocolVersion = b.launchProtocolVersion;
      // Free-form orc CLI args replace the individual flag knobs; persist when non-empty.
      const orchestratorArgs = b.orchestratorArgs?.trim();
      if (orchestratorArgs) backend.orchestratorArgs = orchestratorArgs;
      // Observability knobs are delivered as env vars (not CLI args), kept typed.
      if (b.streamingTarzst) backend.streamingTarzst = true;
      if (b.otelEndpoint) backend.otelEndpoint = b.otelEndpoint;
      if (b.otelAuth) backend.otelAuth = b.otelAuth;
      if (b.awsSubnets) backend.awsSubnets = b.awsSubnets.split(',').map(s => s.trim()).filter(Boolean);
      if (b.awsSecurityGroups) backend.awsSecurityGroups = b.awsSecurityGroups.split(',').map(s => s.trim()).filter(Boolean);
      if (b.awsInstanceProfile) backend.awsInstanceProfile = b.awsInstanceProfile;
      if (b.gcpProjectId) backend.gcpProjectId = b.gcpProjectId;
      if (b.gcpSubnetwork) backend.gcpSubnetwork = b.gcpSubnetwork;
      if (b.gcpFirewallTags) backend.gcpFirewallTags = b.gcpFirewallTags.split(',').map(s => s.trim()).filter(Boolean);
      if (b.gcpServiceAccount) backend.gcpServiceAccount = b.gcpServiceAccount;
      if (b.cwApiServer) backend.cwApiServer = b.cwApiServer;
      if (b.cwKubeToken) backend.cwKubeToken = b.cwKubeToken;
      if (b.cwNamespace) backend.cwNamespace = b.cwNamespace;
      if (b.cwGpuCount != null) backend.cwGpuCount = b.cwGpuCount;
      if (b.cwCpuCount != null) backend.cwCpuCount = b.cwCpuCount;
      if (b.cwMemoryGb != null) backend.cwMemoryGb = b.cwMemoryGb;
      if (b.cwRootDiskSize) backend.cwRootDiskSize = b.cwRootDiskSize;
      if (b.cwRootPvcLabel) {
        const labels: Record<string, string> = {};
        b.cwRootPvcLabel.split(',').forEach(pair => {
          const [k, v] = pair.split('=').map(s => s.trim());
          if (k && v) labels[k] = v;
        });
        if (Object.keys(labels).length > 0) backend.cwRootPvcLabel = labels;
      }
      if (b.cwAdditionalDiskSize) {
        backend.cwAdditionalDisks = [{ name: 'data', size: b.cwAdditionalDiskSize }];
      }
      if (b.cwVsUsername) backend.cwVsUsername = b.cwVsUsername;
      if (b.cwVsPassword) backend.cwVsPassword = b.cwVsPassword;
      return backend;
    }),
    allocationStrategy: infra.allocationStrategy,
  };
}

function parseInfrastructure(settingsJson: string | null): InfrastructureFormData | undefined {
  if (!settingsJson) return undefined;
  try {
    const parsed = JSON.parse(settingsJson);
    if (!parsed.backends || !Array.isArray(parsed.backends)) return undefined;
    return {
      backends: parsed.backends.map((b: any) => ({
        id: b.id || `${b.cloud || 'aws'}-${(b.region || 'us-east-1').replace(/[-_]/g, '')}-${id3()}`,
        cloud: b.cloud || 'aws',
        region: b.region || 'us-east-1',
        instanceType: b.instanceType || '',
        imageId: b.imageId || '',
        weight: b.weight ?? 1,
        maxCapacity: b.maxCapacity,
        maxConcurrent: b.maxConcurrent,
        startupMode: b.startupMode,
        launchProtocolVersion: b.launchProtocolVersion,
        // Prefer an explicit free-form args string; otherwise convert the legacy
        // typed flags so older pool configs migrate losslessly (incl. deprecated
        // flags retained for pools pinned to earlier orc versions).
        orchestratorArgs: b.orchestratorArgs ?? legacyFlagsToOrchestratorArgs(b),
        // Observability knobs stay typed (env-var delivered, not CLI args).
        streamingTarzst: b.streamingTarzst,
        otelEndpoint: b.otelEndpoint,
        otelAuth: b.otelAuth,
        awsSubnets: Array.isArray(b.awsSubnets) ? b.awsSubnets.join(', ') : b.awsSubnets,
        awsSecurityGroups: Array.isArray(b.awsSecurityGroups) ? b.awsSecurityGroups.join(', ') : b.awsSecurityGroups,
        awsInstanceProfile: b.awsInstanceProfile,
        gcpProjectId: b.gcpProjectId,
        gcpSubnetwork: b.gcpSubnetwork,
        gcpFirewallTags: Array.isArray(b.gcpFirewallTags) ? b.gcpFirewallTags.join(', ') : b.gcpFirewallTags,
        gcpServiceAccount: b.gcpServiceAccount,
      })),
      allocationStrategy: parsed.allocationStrategy || 'balanced',
    };
  } catch {
    return undefined;
  }
}

interface EditProfileDialogProps {
  isOpen: boolean;
  profile: Profile;
  allowMediated?: boolean;
  isPlatformAdmin?: boolean;
  infraDefaults?: unknown;
  onClose: () => void;
  onUpdate: (profileId: string, data: Omit<ProfileUpdateReq, 'id'>) => Promise<{ success: boolean; error?: string }>;
}

export function EditProfileDialog({
  isOpen,
  profile,
  allowMediated = false,
  isPlatformAdmin = false,
  infraDefaults,
  onClose,
  onUpdate
}: EditProfileDialogProps) {
  const existingSchedule = useMemo((): CapacitySchedule | undefined => {
    if (!profile.capacityScheduleJson) return undefined;
    try {
      return JSON.parse(profile.capacityScheduleJson);
    } catch {
      return undefined;
    }
  }, [profile.capacityScheduleJson]);

  const existingInfrastructure = useMemo(() => {
    return parseInfrastructure(profile.settingsJson ?? null);
  }, [profile.settingsJson]);

  const [formData, setFormData] = useState<ProfileFormData>({
    name: profile.name,
    profileSlug: profile.profileSlug || '',
    type: profile.type,
    region: profile.region,
    notes: profile.notes || '',
    active: Boolean(profile.active),
    capacitySchedule: existingSchedule,
    infrastructure: existingInfrastructure,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      let schedule: CapacitySchedule | undefined;
      if (profile.capacityScheduleJson) {
        try {
          schedule = JSON.parse(profile.capacityScheduleJson);
        } catch {
          schedule = undefined;
        }
      }
      setFormData({
        name: profile.name,
        profileSlug: profile.profileSlug || '',
        type: profile.type,
        region: profile.region,
        notes: profile.notes || '',
        active: Boolean(profile.active),
        capacitySchedule: schedule,
        infrastructure: parseInfrastructure(profile.settingsJson ?? null),
      });
      setErrors({});
    }
  }, [isOpen, profile]);

  const handleChange = useCallback((field: keyof ProfileFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  }, [errors]);

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();

    const validation = profileService.validateProfileForm(formData);
    if (!validation.valid) {
      setErrors(validation.errors);
      return;
    }

    setIsSubmitting(true);
    setErrors({});

    try {
      const updates: Omit<ProfileUpdateReq, 'id'> = {};
      if (formData.name !== profile.name) updates.name = formData.name;
      if (formData.type !== profile.type) updates.type = formData.type;
      if (formData.region !== profile.region) updates.region = formData.region;
      if ((formData.notes || '') !== (profile.notes || '')) updates.notes = formData.notes;

      // Capacity schedule
      const currentScheduleJson = formData.capacitySchedule ? JSON.stringify(formData.capacitySchedule) : undefined;
      const originalScheduleJson = profile.capacityScheduleJson || undefined;
      if (currentScheduleJson !== originalScheduleJson) {
        updates.capacityScheduleJson = formData.capacitySchedule;
      }

      // Infrastructure settings (only admins can modify)
      if (isPlatformAdmin && formData.infrastructure) {
        const currentSettingsJson = JSON.stringify(buildSettingsJson(formData.infrastructure));
        const originalSettingsJson = profile.settingsJson || undefined;
        if (currentSettingsJson !== originalSettingsJson) {
          updates.settingsJson = buildSettingsJson(formData.infrastructure);
        }
      }

      const result = await onUpdate(profile.id, updates);

      if (result.success) {
        onClose();
      } else {
        setErrors({ submit: result.error || 'Failed to update profile' });
      }
    } catch (error: any) {
      setErrors({ submit: error.message || 'Failed to update profile' });
    } finally {
      setIsSubmitting(false);
    }
  }, [formData, profile, onUpdate, onClose]);

  const handleClose = useCallback(() => {
    if (!isSubmitting) {
      setErrors({});
      onClose();
    }
  }, [isSubmitting, onClose]);

  const handleCapacityScheduleChange = useCallback((schedule: CapacitySchedule) => {
    setFormData(prev => ({ ...prev, capacitySchedule: schedule }));
  }, []);

  const handleInfrastructureChange = useCallback((infra: InfrastructureFormData) => {
    setFormData(prev => ({ ...prev, infrastructure: infra }));
  }, []);

  const isPrivatePool = formData.type === 'private_pool';

  return (
    <Modal
      isOpen={isOpen}
      title="Edit Dedicated Pool"
      subtitle={<span>Editing <strong>{profile.name}</strong></span>}
      onClose={handleClose}
      size="large"
    >
      <form onSubmit={handleSubmit}>
        {isPlatformAdmin && (
          <FormField
            label="Pool Name"
            htmlFor="edit-profile-name"
            required
            error={errors.name}
          >
            <input
              id="edit-profile-name"
              type="text"
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              autoFocus
              disabled={isSubmitting}
            />
          </FormField>
        )}

        <FormField
          label="Super Region"
          htmlFor="edit-profile-region"
          required
          error={errors.region}
        >
          <select
            id="edit-profile-region"
            value={formData.region}
            onChange={(e) => handleChange('region', e.target.value)}
            disabled={isSubmitting}
          >
            {SUPER_REGION_OPTIONS.map(r => (
              <option key={r.value} value={r.value}>{r.label}</option>
            ))}
          </select>
        </FormField>

        {isPrivatePool && (
          <div style={{ marginBottom: 'var(--space-4)' }}>
            <CapacityScheduleEditor
              value={formData.capacitySchedule}
              onChange={handleCapacityScheduleChange}
              allowMediated={allowMediated}
              disabled={isSubmitting}
            />
          </div>
        )}

        {isPlatformAdmin && formData.infrastructure && (
          <div style={{ marginBottom: 'var(--space-4)' }}>
            <InfrastructureEditor
              value={formData.infrastructure}
              onChange={handleInfrastructureChange}
              disabled={isSubmitting}
              infraDefaults={infraDefaults}
            />
          </div>
        )}

        {isPlatformAdmin && (
          <FormField
            label="Notes"
            htmlFor="edit-profile-notes"
          >
            <textarea
              id="edit-profile-notes"
              value={formData.notes}
              onChange={(e) => handleChange('notes', e.target.value)}
              rows={3}
              disabled={isSubmitting}
            />
          </FormField>
        )}

        {errors.submit && (
          <div style={{ color: 'var(--gray-10)', marginBottom: 'var(--space-4)' }}>
            {errors.submit}
          </div>
        )}

        <div style={{ display: 'flex', gap: 'var(--space-3)', justifyContent: 'flex-end' }}>
          <button
            type="button"
            onClick={handleClose}
            disabled={isSubmitting}
            style={{
              padding: 'var(--space-2) var(--space-4)',
              border: 'var(--border-width) solid var(--border)',
              background: 'var(--bg-primary)',
              cursor: isSubmitting ? 'not-allowed' : 'pointer'
            }}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            style={{
              padding: 'var(--space-2) var(--space-4)',
              border: 'var(--border-width) solid var(--gray-10)',
              background: 'var(--gray-10)',
              color: 'var(--gray-0)',
              cursor: isSubmitting ? 'not-allowed' : 'pointer'
            }}
          >
            {isSubmitting ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
